// =============================================================================
// Workspace Store (Zustand)
// Central state management for the workspace
// =============================================================================

import { create } from 'zustand';
import { mergeDemoWorkspace, DEMO_PROJECT_ID } from '@shared/domain';
import {
  Workspace,
  ProjectContext,
  Evaluation,
  Sipoc,
  Moscow,
  Raci,
  DealRoom,
  CraMonth,
  TreasuryProfile,
  UserProfile,
  CalcMissionSession,
  Estimation,
  WorkspaceNote,
  MissionCanvas,
} from '@shared/types';
import { loadWorkspace, saveWorkspace, resetWorkspace, hasExistingWorkspace, createDefaultWorkspace } from '@shared/storage';
import { listCras } from '@shared/storage/repositories/craRepository';
import { getActiveProjects } from '@shared/domain/projects';
import { exportFullBackup, importFullBackup } from '@shared/storage/backup';
import { encryptBackup, decryptBackup, generateRecoveryKey, recoveryKeyHint } from '@shared/crypto/backupCrypto';
import { uploadBackup, downloadBackup } from '@shared/api/backup';

export interface BackupState {
  status: 'idle' | 'backing-up' | 'restoring' | 'success' | 'error';
  error?: string;
  lastBackupAt?: number;
}

interface WorkspaceState extends Workspace {
  // Computed
  currentProject: ProjectContext | null;
  activeMission: () => ProjectContext | undefined;
  
  // Backup state
  backupState: BackupState;
  setBackupState: (state: Partial<BackupState>) => void;
  backupToCloud: () => Promise<string>;
  restoreFromCloud: (backupId: string, recoveryKey: string) => Promise<void>;
  
  // Actions
  setTheme: (theme: 'dark' | 'light') => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  replaceProfile: (profile: UserProfile) => void;
  setCurrentProjectId: (id: string | undefined) => void;
  setCurrentCalcMissionSessionId: (id: string | undefined) => void;
  addProject: (project: ProjectContext) => void;
  updateProject: (id: string, updates: Partial<ProjectContext>) => void;
  deleteProject: (id: string) => void;
  archiveProject: (id: string) => void;
  restoreProject: (id: string) => void;
  /**
   * LOT 4 — action canonique unique pour masquer la mission démo, quel que
   * soit l'écran d'où elle est déclenchée. Persiste (via `meta.demoDismissed`,
   * déjà posé par `deleteProject`) pour ne jamais être réinjectée après un
   * reload. Ce n'est PAS une suppression de donnée utilisateur normale : ne
   * jamais l'utiliser pour autre chose que DEMO_PROJECT_ID.
   */
  hideDemoMission: () => void;
  
  addEvaluation: (evaluation: Evaluation) => void;
  updateEvaluation: (id: string, updates: Partial<Evaluation>) => void;
  
  addSipoc: (sipoc: Sipoc) => void;
  updateSipoc: (id: string, updates: Partial<Sipoc>) => void;
  
  addMoscow: (moscow: Moscow) => void;
  updateMoscow: (id: string, updates: Partial<Moscow>) => void;
  
  addRaci: (raci: Raci) => void;
  updateRaci: (id: string, updates: Partial<Raci>) => void;

  addDealRoom: (dealRoom: DealRoom) => void;
  updateDealRoom: (id: string, updates: Partial<DealRoom>) => void;

  addCra: (cra: CraMonth) => void;
  updateCra: (id: string, updates: Partial<CraMonth>) => void;

  addTreasury: (treasury: TreasuryProfile) => void;
  updateTreasury: (id: string, updates: Partial<TreasuryProfile>) => void;

  addCalcMissionSession: (session: CalcMissionSession) => void;
  updateCalcMissionSession: (id: string, updates: Partial<CalcMissionSession>) => void;
  deleteCalcMissionSession: (id: string) => void;

  addEstimation: (estimation: Estimation) => void;
  updateEstimation: (id: string, updates: Partial<Estimation>) => void;
  deleteEstimation: (id: string) => void;

  addNote: (note: WorkspaceNote) => void;
  updateNote: (id: string, updates: Partial<WorkspaceNote>) => void;
  deleteNote: (id: string) => void;

  upsertCanvas: (canvas: MissionCanvas) => void;
  removeCanvas: (id: string) => void;
  canvasByProjectId: (projectId: string) => MissionCanvas | undefined;

  setActiveMission: (id: string | null) => void;
  setMissionStatus: (projectId: string, status: NonNullable<ProjectContext['missionStatus']>) => void;

  setVariables: (vars: Record<string, number>) => void;
  updateVariable: (key: string, value: number) => void;
  deleteVariable: (key: string) => void;

  seedDemoProject: () => void;
  
  // Hydration
  hydrate: () => void;
  syncFromDexie: () => Promise<void>;
  reset: () => void;
  save: () => boolean;
}

function normalizeInitialWorkspace(workspace: Workspace): Workspace {
  const firstActiveProjectId = workspace.projects.find((project) => !project.archivedAt)?.id ?? null;
  const activeMissionId = workspace.activeMissionId && workspace.projects.some((project) => project.id === workspace.activeMissionId && !project.archivedAt)
    ? workspace.activeMissionId
    : firstActiveProjectId;
  const currentProjectId = workspace.currentProjectId && workspace.projects.some((project) => project.id === workspace.currentProjectId && !project.archivedAt)
    ? workspace.currentProjectId
    : activeMissionId ?? undefined;

  return {
    ...workspace,
    activeMissionId,
    currentProjectId,
  };
}

/**
 * Create a new workspace store with hydration from localStorage
 * This is a factory function to allow for SSR compatibility in the future
 */
export const createWorkspaceStore = () => {
  const initialWorkspace = normalizeInitialWorkspace(hasExistingWorkspace() ? loadWorkspace() : createDefaultWorkspace());

  function readLastBackupAt(): number | undefined {
    try {
      const val = localStorage.getItem('indxone.cloud.lastBackupAt');
      return val ? Number(val) : undefined;
    } catch { return undefined; }
  }

  return create<WorkspaceState>()((set, get) => ({
    ...initialWorkspace,
    backupState: { status: 'idle', lastBackupAt: readLastBackupAt() },
    
    // Computed
    get currentProject() {
      const { currentProjectId, projects } = get();
      if (!currentProjectId) return null;
      return projects.find((p) => p.id === currentProjectId && !p.archivedAt) ?? null;
    },

    activeMission: () => {
      const { activeMissionId, currentProjectId, projects } = get();
      const targetId = activeMissionId ?? currentProjectId;
      if (!targetId) return undefined;
      return projects.find((p) => p.id === targetId && !p.archivedAt);
    },

    updateProfile: (updates) => {
      set((state) => ({
        profile: {
          ...state.profile,
          ...updates,
          updatedAt: Date.now(),
        },
      }));
      get().save();
    },

    setTheme: (theme) => {
      set((state) => ({
        preferences: {
          ...state.preferences,
          theme,
        },
      }));
      get().save();
    },

    replaceProfile: (profile) => {
      set({ profile: { ...profile, updatedAt: Date.now() } });
      get().save();
    },
    
    // Project actions
    setCurrentProjectId: (id) => {
      set({ currentProjectId: id });
      get().save();
    },

    setCurrentCalcMissionSessionId: (id) => {
      set({ currentCalcMissionSessionId: id });
      get().save();
    },
    
    addProject: (project) => {
      set((state) => ({
        projects: [...state.projects, project],
        currentProjectId:
          state.currentProjectId && state.projects.some((item) => item.id === state.currentProjectId && !item.archivedAt)
            ? state.currentProjectId
            : project.id,
      }));
      get().save();
    },
    
    updateProject: (id, updates) => {
      set((state) => ({
        projects: state.projects.map(p => p.id === id ? { ...p, ...updates, updatedAt: Date.now() } : p),
      }));
      get().save();
    },

    archiveProject: (id) => {
      set((state) => {
        const updatedAt = Date.now();
        const projects = state.projects.map((project) => (
          project.id === id
            ? { ...project, archivedAt: project.archivedAt || updatedAt, updatedAt }
            : project
        ));
        const activeProjects = getActiveProjects(projects);
        return {
          projects,
          currentProjectId: state.currentProjectId === id ? activeProjects[0]?.id : state.currentProjectId,
        };
      });
      get().save();
    },

    restoreProject: (id) => {
      set((state) => {
        const updatedAt = Date.now();
        const projects = state.projects.map((project) => (
          project.id === id
            ? { ...project, archivedAt: undefined, updatedAt }
            : project
        ));
        const currentProject = state.currentProjectId
          ? projects.find((project) => project.id === state.currentProjectId && !project.archivedAt)
          : null;
        const currentProjectId = currentProject?.id ?? id;
        return {
          projects,
          currentProjectId: currentProjectId,
        };
      });
      get().save();
    },
    
    deleteProject: (id) => {
      set((state) => ({
        projects: state.projects.filter(p => p.id !== id),
        evaluations: state.evaluations.filter(e => e.projectId !== id),
        sipocs: state.sipocs.filter(s => s.projectId !== id),
        moscows: state.moscows.filter(m => m.projectId !== id),
        racis: state.racis.filter(r => r.projectId !== id),
        dealRooms: state.dealRooms.filter(d => d.projectId !== id),
        cras: state.cras.filter(c => c.projectId !== id),
        treasuries: state.treasuries.filter(t => t.projectId !== id),
        canvases: state.canvases.filter(c => c.projectId !== id),
        calcMissionSessions: state.calcMissionSessions.filter((session) => session.projectId !== id),
        currentProjectId: state.currentProjectId === id
          ? getActiveProjects(state.projects.filter((project) => project.id !== id))[0]?.id
          : state.currentProjectId,
        activeMissionId: state.activeMissionId === id ? null : state.activeMissionId,
        ...(id === DEMO_PROJECT_ID ? { meta: { ...state.meta, demoDismissed: true } } : {}),
      }));
      get().save();
    },

    hideDemoMission: () => {
      get().deleteProject(DEMO_PROJECT_ID);
    },
    
    // Evaluation actions
    addEvaluation: (evaluation) => {
      set((state) => ({
        evaluations: [...state.evaluations.filter(e => e.id !== evaluation.id), evaluation],
      }));
      get().save();
    },
    
    updateEvaluation: (id, updates) => {
      set((state) => ({
        evaluations: state.evaluations.map(e => e.id === id ? { ...e, ...updates } : e),
      }));
      get().save();
    },
    
    // SIPOC actions
    addSipoc: (sipoc) => {
      set((state) => ({
        sipocs: [...state.sipocs.filter(item => item.id !== sipoc.id), sipoc],
      }));
      get().save();
    },
    
    updateSipoc: (id, updates) => {
      set((state) => ({
        sipocs: state.sipocs.map(s => s.id === id ? { ...s, ...updates, updatedAt: Date.now() } : s),
      }));
      get().save();
    },
    
    // MoSCoW actions
    addMoscow: (moscow) => {
      set((state) => ({
        moscows: [...state.moscows.filter(item => item.id !== moscow.id), moscow],
      }));
      get().save();
    },
    
    updateMoscow: (id, updates) => {
      set((state) => ({
        moscows: state.moscows.map(m => m.id === id ? { ...m, ...updates, updatedAt: Date.now() } : m),
      }));
      get().save();
    },
    
    // RACI actions
    addRaci: (raci) => {
      set((state) => ({
        racis: [...state.racis.filter(item => item.id !== raci.id), raci],
      }));
      get().save();
    },
    
    updateRaci: (id, updates) => {
      set((state) => ({
        racis: state.racis.map(r => r.id === id ? { ...r, ...updates, updatedAt: Date.now() } : r),
      }));
      get().save();
    },

    addDealRoom: (dealRoom) => {
      set((state) => ({
        dealRooms: [...state.dealRooms.filter(item => item.id !== dealRoom.id), dealRoom],
      }));
      get().save();
    },

    updateDealRoom: (id, updates) => {
      set((state) => ({
        dealRooms: state.dealRooms.map(d => d.id === id ? { ...d, ...updates, updatedAt: Date.now() } : d),
      }));
      get().save();
    },

    addCra: (cra) => {
      set((state) => ({
        cras: [
          ...state.cras.filter(item => item.id !== cra.id),
          cra,
        ],
      }));
      get().save();
    },

    updateCra: (id, updates) => {
      set((state) => ({
        cras: state.cras.map(c => c.id === id ? { ...c, ...updates, updatedAt: Date.now() } : c),
      }));
      get().save();
    },

    addTreasury: (treasury) => {
      set((state) => ({
        treasuries: [...state.treasuries.filter(item => item.id !== treasury.id), treasury],
      }));
      get().save();
    },

    updateTreasury: (id, updates) => {
      set((state) => ({
        treasuries: state.treasuries.map(t => t.id === id ? { ...t, ...updates, updatedAt: Date.now() } : t),
      }));
      get().save();
    },

    addCalcMissionSession: (session) => {
      set((state) => ({
        calcMissionSessions: [...state.calcMissionSessions.filter((item) => item.id !== session.id), session],
        currentCalcMissionSessionId: session.id,
      }));
      get().save();
    },

    updateCalcMissionSession: (id, updates) => {
      set((state) => ({
        calcMissionSessions: state.calcMissionSessions.map((session) =>
          session.id === id ? { ...session, ...updates, updatedAt: Date.now() } : session,
        ),
      }));
      get().save();
    },

    deleteCalcMissionSession: (id) => {
      set((state) => {
        const remaining = state.calcMissionSessions.filter((session) => session.id !== id);
        return {
          calcMissionSessions: remaining,
          currentCalcMissionSessionId: state.currentCalcMissionSessionId === id ? remaining[0]?.id : state.currentCalcMissionSessionId,
        };
      });
      get().save();
    },

    addEstimation: (estimation) => {
      set((state) => ({
        estimations: [...state.estimations.filter((e) => e.id !== estimation.id), estimation],
      }));
      get().save();
    },

    updateEstimation: (id, updates) => {
      set((state) => ({
        estimations: state.estimations.map((e) =>
          e.id === id ? { ...e, ...updates, updatedAt: Date.now() } : e,
        ),
      }));
      get().save();
    },

    deleteEstimation: (id) => {
      set((state) => ({ estimations: state.estimations.filter((e) => e.id !== id) }));
      get().save();
    },

    addNote: (note) => {
      set((state) => ({
        notes: [...state.notes.filter((n) => n.id !== note.id), note],
      }));
      get().save();
    },

    updateNote: (id, updates) => {
      set((state) => ({
        notes: state.notes.map((n) =>
          n.id === id ? { ...n, ...updates, updatedAt: Date.now() } : n,
        ),
      }));
      get().save();
    },

    deleteNote: (id) => {
      set((state) => ({ notes: state.notes.filter((n) => n.id !== id) }));
      get().save();
    },

    upsertCanvas: (canvas) => {
      set((state) => ({
        canvases: [...state.canvases.filter((c) => c.id !== canvas.id), canvas],
        projects: state.projects.map((p) =>
          p.id === canvas.projectId && p.canvasId !== canvas.id
            ? { ...p, canvasId: canvas.id, updatedAt: Date.now() }
            : p,
        ),
      }));
      get().save();
    },

    removeCanvas: (id) => {
      set((state) => ({ canvases: state.canvases.filter((c) => c.id !== id) }));
      get().save();
    },

    canvasByProjectId: (projectId) => {
      return get().canvases.find((c) => c.projectId === projectId);
    },

    setActiveMission: (id) => {
      set({ activeMissionId: id, ...(id !== null ? { currentProjectId: id } : {}) });
      get().save();
    },

    setMissionStatus: (projectId, status) => {
      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === projectId ? { ...p, missionStatus: status, updatedAt: Date.now() } : p,
        ),
      }));
      get().save();
    },

    setVariables: (vars) => {
      set({ variables: vars });
      get().save();
    },

    updateVariable: (key, value) => {
      set((state) => ({ variables: { ...state.variables, [key]: value } }));
      get().save();
    },

    deleteVariable: (key) => {
      set((state) => {
        const { [key]: _, ...rest } = state.variables;
        return { variables: rest };
      });
      get().save();
    },

    seedDemoProject: () => {
      const state = get();
      const { currentProject: _currentProject, ...workspace } = state;
      const merged = mergeDemoWorkspace(workspace as Workspace);
      set({ ...merged });
      get().save();
    },
    
    // Hydration - load from storage
    hydrate: () => {
      const workspace = normalizeInitialWorkspace(loadWorkspace());
      set({ ...workspace });
    },

    // Sync Dexie-backed data (CRAs) into Zustand for consumers that still read from Zustand
    syncFromDexie: async () => {
      try {
        const dexieCras = await listCras();
        if (dexieCras.length === 0) return;
        set((state) => {
          const merged = [...state.cras];
          for (const cra of dexieCras) {
            const idx = merged.findIndex((c) => c.id === cra.id);
            if (idx >= 0) merged[idx] = cra;
            else merged.push(cra);
          }
          return { cras: merged, meta: { ...state.meta, updatedAt: Date.now() } };
        });
      } catch {
        // Dexie non disponible (SSR/test), ignorer
      }
    },
    
    // Reset - clear everything
    reset: () => {
      const freshWorkspace = resetWorkspace();
      set({ ...normalizeInitialWorkspace(freshWorkspace) });
    },
    
    // Save - persist to storage, update meta.updatedAt in memory for sync subscribers
    save: () => {
      const state = get();
      const { currentProject: _currentProject, ...workspace } = state;
      const updatedAt = Date.now();
      set({ meta: { ...state.meta, updatedAt } });
      return saveWorkspace({ ...workspace, meta: { ...workspace.meta, updatedAt } } as Workspace);
    },

    setBackupState: (partial) => {
      set((state) => ({ backupState: { ...state.backupState, ...partial } }));
    },

    backupToCloud: async () => {
      set((state) => ({ backupState: { ...state.backupState, status: 'backing-up', error: undefined } }));
      try {
        const recoveryKey = generateRecoveryKey();
        const payload = await exportFullBackup();
        const { encrypted, iv, salt } = await encryptBackup(payload, recoveryKey);
        const encryptedHex = Array.from(new Uint8Array(encrypted), (b) => b.toString(16).padStart(2, '0')).join('');
        const ivHex = Array.from(iv, (b) => b.toString(16).padStart(2, '0')).join('');
        const saltHex = Array.from(salt, (b) => b.toString(16).padStart(2, '0')).join('');
        const hint = recoveryKeyHint(recoveryKey);
        await uploadBackup(encryptedHex, ivHex, saltHex, hint);
        const now = Date.now();
        try { localStorage.setItem('indxone.cloud.lastBackupAt', String(now)); } catch { /* noop */ }
        set({ backupState: { status: 'success', lastBackupAt: now } });
        return recoveryKey;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur backup cloud';
        set({ backupState: { status: 'error', error: message } });
        throw error;
      }
    },

    restoreFromCloud: async (backupId, recoveryKey) => {
      set((state) => ({ backupState: { ...state.backupState, status: 'restoring', error: undefined } }));
      try {
        const data = await downloadBackup(backupId);
        const encrypted = new Uint8Array(data.encrypted.match(/.{1,2}/g)!.map((b) => parseInt(b, 16))).buffer as ArrayBuffer;
        const salt = new Uint8Array(data.salt.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)));
        const iv = new Uint8Array(data.iv.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)));
        const payload = await decryptBackup(encrypted, recoveryKey, salt, iv);
        await importFullBackup(payload as Parameters<typeof importFullBackup>[0]);
        set({ backupState: { status: 'success' } });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur restauration';
        set({ backupState: { status: 'error', error: message } });
        throw error;
      }
    },
  }));
};

// Create the global store instance
export const useWorkspaceStore = createWorkspaceStore();

// Selectors for better performance
export const useCurrentProject = () =>
  useWorkspaceStore((state) => {
    if (!state.currentProjectId) return null;
    return state.projects.find((project) => project.id === state.currentProjectId && !project.archivedAt) ?? null;
  });
export const useProjects = () => useWorkspaceStore(state => state.projects);
export const useActiveProjects = () => useWorkspaceStore((state) => getActiveProjects(state.projects));
export const useArchivedProjects = () => useWorkspaceStore((state) => state.projects.filter((project) => project.archivedAt));
export const useEvaluations = () => useWorkspaceStore(state => state.evaluations);
export const useSipocs = () => useWorkspaceStore(state => state.sipocs);
export const useMoscows = () => useWorkspaceStore(state => state.moscows);
export const useRacis = () => useWorkspaceStore(state => state.racis);
export const useDealRooms = () => useWorkspaceStore(state => state.dealRooms);
export const useCras = () => useWorkspaceStore(state => state.cras);
export const useTreasuries = () => useWorkspaceStore(state => state.treasuries);
export const useProfile = () => useWorkspaceStore(state => state.profile);
export const useCalcMissionSessions = () => useWorkspaceStore(state => state.calcMissionSessions);
export const useEstimations = () => useWorkspaceStore(state => state.estimations);
export const useNotes = () => useWorkspaceStore(state => state.notes);
export const useWorkspaceMeta = () => useWorkspaceStore(state => state.meta);
export const useVariables = () => useWorkspaceStore(state => state.variables);
export const useCanvases = () => useWorkspaceStore(state => state.canvases);
export const useActiveMission = () =>
  useWorkspaceStore((state) =>
    state.activeMissionId
      ? state.projects.find((p) => p.id === state.activeMissionId && !p.archivedAt) ?? null
      : state.currentProjectId
        ? state.projects.find((p) => p.id === state.currentProjectId && !p.archivedAt) ?? null
        : null,
  );
